import { useCallback, useEffect, useState, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';
import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { CUSTOMER_TYPE_TRADE, CUSTOMER_TYPE_VIP } from '../constants/userConstants';
import {
    getUserZipCode,
    getUserSessionCountryCode,
    getUserSessionRegions,
    getRegionsList,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import {
    setUserRegionalInfo,
    forceSetUserRegionalInfo,
} from 'dibs-regional-info/exports/setUserRegionalInfo';
import serverVars from 'server-vars';
import { isPersonalizedRerankAbTestVariant } from '../utils/abTestHelper';

type RelayVariables = {
    fetchUser: boolean;
    updateAuctionItem: boolean;
    updateItemPricing: boolean;
    updatedUserCountryCode: string;
};
type HeaderVariables = {
    updateUserState?: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
    header: {
        updateUserState?: boolean;
    };
};

type UserRelayVars = {
    userId: string;
    personalizationId?: string;
    guestId?: string;
    fetchUser: boolean;
    userIds: string[];
    fetchFollowSearch: boolean;
    isTrade: boolean;
    isVip: boolean;
    userZipCode: string;
    userCountryCode: string;
    fetchRegionalInfo: boolean;
    regionsList?: string[];
    fetchSb?: boolean;
};

type Props = {
    onUserRefetch: (
        vars: UserRelayVars & { isClient: boolean },
        renderVariables: (UserRelayVars & { isClient: boolean }) | null,
        callback: (err: Error, props: Record<string, unknown>) => void
    ) => void;
    shouldUpdateRelayVariables?: boolean;
    refreshPageOnTradeLogin?: boolean;
};

export const SharedUserAndRelayVarLoader: FC<Props> = ({
    onUserRefetch,
    shouldUpdateRelayVariables = true,
    refreshPageOnTradeLogin = true,
}) => {
    const environment = useRelayEnvironment();
    const dispatch = useDispatch();
    const { fetchUser, updateAuctionItem, updateItemPricing, updatedUserCountryCode } = useSelector<
        ReduxStoreType,
        RelayVariables
    >(state => {
        /**
         * updateUserState is used for responsive. The authentication is on the navigation is
         * managed in dbl, so we need to know when a user has successfully logged in/registered
         *
         * fetchUser is used for mobile. The mobile nav works a little bit differently from dbl.
         * It calls a callback when a user clicks an auth button and we handle all of the auth
         * flow in `app-buyer-finding`.
         *
         * TODO: Make sure they work the same way.
         * NOTE: updateUserState does not seem to exist, even for responsive it is using fetchUser
         */
        return {
            fetchUser: state.header.updateUserState || state.relayVariables.variables.fetchUser,
            updateAuctionItem: !!state.relayVariables.variables.updateAuctionItem || false,
            updateItemPricing: !!state.relayVariables.variables.updateItemPricing || false,
            updatedUserCountryCode: state.relayVariables.variables.updatedUserCountryCode || '',
        };
    });

    const [userFetched, setUserFetched] = useState(false);

    const triggerRefetch = useCallback(async (): Promise<void> => {
        const getUserRelayVars = async (): Promise<UserRelayVars> => {
            /**
             * Must default to an empty string! Because the default value for userId in the
             * relay query is set to an empty string. Defaulting to anything different will ruin
             * your day, trust me ;)
             */
            const userId = getBuyerId(document.cookie) || '';
            const userType = getUserType(document.cookie);

            /**
             * check and set (if missing) user regional info in sessionStorage
             * this is needed when user opens a new tab, and sessionStorage is still empty
             */
            // await is needed here! It's needed to make sure to wait till
            // sessionStorage.userGeoInfo has enough data to continue
            // this function execution where userZipCode, userCountryCode and hasSessionRegions
            // is being set a few lines down
            if (environment) {
                if (userId) {
                    await forceSetUserRegionalInfo({
                        relayEnv: environment,
                        cookieDomain: serverVars.get('cookieDomain'),
                        userId,
                    });
                } else {
                    await setUserRegionalInfo({
                        relayEnv: environment,
                        cookieDomain: serverVars.get('cookieDomain'),
                        userId,
                    });
                }
            }

            const userZipCode = getUserZipCode();
            const userCountryCode = updatedUserCountryCode || getUserSessionCountryCode();
            const hasSessionRegions = !!getUserSessionRegions();
            return {
                userId,
                fetchUser: !!userId,
                userIds: userId ? [userId] : [],
                fetchFollowSearch: !!userId,
                isTrade: userType === CUSTOMER_TYPE_TRADE,
                isVip: userType === CUSTOMER_TYPE_VIP,
                userZipCode,
                userCountryCode,
                fetchRegionalInfo: !hasSessionRegions,
                fetchSbOnUserChange: false,
                ...(isPersonalizedRerankAbTestVariant()
                    ? {
                          fetchSbOnUserChange: true, //If user logs in, personalized-rerank needs to trigger s&b refetch
                          personalizationId: userId,
                          guestId: getGuestId(document.cookie) || '',
                          regionsList: getRegionsList(),
                      }
                    : {}),
            };
        };

        const variables = {
            ...(await getUserRelayVars()),
            isClient: true,
        };

        onUserRefetch(variables, null, (err, additionalVariables) => {
            /**
             * Mimick relay behaviour - only change the variables after a successful refetch.
             */
            if (err) {
                setUserFetched(false);
            } else if (shouldUpdateRelayVariables) {
                dispatch(
                    updateRelayVariables({
                        ...variables,
                        ...additionalVariables,
                        updateAuctionItem: false,
                        updateItemPricing: false,
                    })
                );
            }
        });

        setUserFetched(variables.fetchUser);
    }, [dispatch, environment, shouldUpdateRelayVariables, onUserRefetch, updatedUserCountryCode]);
    /**
     * Fetch the user if needed and refetch any fields that depend on `isClient`
     */
    useEffect(() => {
        triggerRefetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        /**
         * First condition: allow for page refreshes from auction pdps
         * Second condition: For cases where the user logs in while browsing the page.
         */
        if (updateAuctionItem) {
            triggerRefetch();
        } else if (fetchUser && !userFetched) {
            const userType = getUserType(document.cookie);
            const isTrade = userType === CUSTOMER_TYPE_TRADE;
            // FINDING-13500: Need to refresh when logging-in as trade user so that
            // "ut" param is appended to url. Without "?ut=trade" in url, some of the
            // trade-specific features, such as filtering by net price, do not work
            if (isTrade && refreshPageOnTradeLogin) {
                window.location.reload();
            } else {
                triggerRefetch();
            }
        } else if (updateItemPricing) {
            triggerRefetch();
        }
    }, [
        fetchUser,
        refreshPageOnTradeLogin,
        triggerRefetch,
        updateAuctionItem,
        updateItemPricing,
        userFetched,
    ]);
    return null;
};
